// ui/blocks/miniplayer.jsx — Global persistent mini audio player bar
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X } from 'lucide-react';
import { usePlayer } from '../../context/playercontext';
import { getApiBaseUrl } from '../../utils/apiConfig';

const MiniPlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentMeditation,
    tehniciMapGlobal,
    isPlaying,
    progress,
    togglePlay,
    stopAndClear,
  } = usePlayer();

  // Only show when there's a meditation AND we're not on its detail page
  const isOnDetailPage =
    currentMeditation &&
    location.pathname === `/meditatie/${currentMeditation.id}`;

  // Only show if meditation was actually played (has progress or is playing)
  const hasBeenPlayed = isPlaying || progress > 0;
  const visible = !!currentMeditation && !isOnDetailPage && hasBeenPlayed;

  const imagine = currentMeditation?.imagine
    ? currentMeditation.imagine.startsWith('http')
      ? currentMeditation.imagine
      : `${getApiBaseUrl()}/img/meditatii/${currentMeditation.imagine}`
    : `${getApiBaseUrl()}/img/meditatii/default.jpg`;

  const tehnicaName = (currentMeditation?.tehnici || []).length
    ? tehniciMapGlobal[String((currentMeditation.tehnici || [])[0])]
    : null;

  const handleBarClick = () => {
    if (currentMeditation) {
      navigate(`/meditatie/${currentMeditation.id}`);
    }
  };

  const handlePlayPause = (e) => {
    e.stopPropagation();
    togglePlay();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="miniplayer"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed left-3 right-3 z-30 font-poppins rounded-2xl overflow-hidden shadow-lg"
          style={{ bottom: 'calc(2rem + 64px + env(safe-area-inset-bottom))' }}
          onClick={handleBarClick}
        >
          {/* Thin progress bar on top edge */}
          <div className="w-full h-0.5 rounded-t-2xl overflow-hidden bg-white/20">
            <div
              className="h-full transition-all duration-150"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, rgba(255,255,255,0.6), rgba(255,255,255,0.9))',
              }}
            />
          </div>

          {/* Main bar */}
          <div
            className="flex items-center gap-3 px-4 py-3 cursor-pointer"
            style={{
              background: '#4A5C47',
            }}
          >
            {/* Thumbnail */}
            <img loading="lazy"
              src={imagine}
              alt=""
              className="w-12 h-12 rounded-xl object-cover object-center flex-shrink-0"
            />

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate leading-tight">
                {currentMeditation?.nume || ''}
              </p>
              <p className="text-white/60 text-xs mt-0.5 truncate">
                {tehnicaName
                  ? `${tehnicaName}${currentMeditation?.durata ? ` · ${currentMeditation.durata} min` : ''}`
                  : currentMeditation?.durata
                  ? `${currentMeditation.durata} min`
                  : ''}
              </p>
            </div>

            {/* Play / Pause button */}
            <button
              onClick={handlePlayPause}
              className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow flex-shrink-0 active:scale-90 transition-transform"
              aria-label={isPlaying ? 'Pauză' : 'Redă'}
            >
              {isPlaying ? (
                <Pause className="w-4 h-4 text-primary-dark" fill="currentColor" />
              ) : (
                <Play className="w-4 h-4 text-primary-dark ml-0.5" fill="currentColor" />
              )}
            </button>

            {/* Close button */}
            <button
              onClick={(e) => { e.stopPropagation(); stopAndClear(); }}
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
              style={{ background: 'rgba(255,255,255,0.12)' }}
              aria-label="Închide player"
            >
              <X className="w-4 h-4 text-white/70" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MiniPlayer;
